import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import type { OwnerDetailsInput, OwnerDetailsWriteOutcome } from '@/profile/OwnerDetailsSection';

export type ManagedOwnerEntry = components['schemas']['ManagedOwnerEntryResponse'];
export type ManagedOwner = components['schemas']['ManagedOwnerResponse'];

/** The active organization, which these core-api routes are gated in. */
export interface Place {
  readonly tenantId: string;
  readonly organizationId: string;
}

const headers = (place: Place) => ({
  'X-Active-Tenant-Id': place.tenantId,
  'X-Active-Organization-Id': place.organizationId,
});

/** Decision 192: the organization's note owners, and whether each has filled in the details. */
export async function listOwners(place: Place): Promise<readonly ManagedOwnerEntry[] | null> {
  try {
    const { data, response } = await apiClient.GET('/v1/owner-details/people', {
      params: { header: headers(place) },
    });
    return data !== undefined && response.ok ? data.items : null;
  } catch {
    return null;
  }
}

export async function readOwner(place: Place, userId: string): Promise<ManagedOwner | null> {
  try {
    const { data, response } = await apiClient.GET('/v1/owner-details/people/{userId}', {
      params: { header: headers(place), path: { userId } },
    });
    return data !== undefined && response.ok ? data : null;
  } catch {
    return null;
  }
}

export async function writeOwner(
  place: Place,
  userId: string,
  next: OwnerDetailsInput | null,
): Promise<OwnerDetailsWriteOutcome> {
  try {
    const { data, error, response } = await apiClient.PUT('/v1/owner-details/people/{userId}', {
      params: { header: headers(place), path: { userId } },
      body: { ownerDetails: next },
    });
    if (error !== undefined || !response.ok || data === undefined) {
      return { ok: false, status: response.status, code: error?.code ?? null };
    }
    return { ok: true, details: data.ownerDetails };
  } catch {
    return { ok: false, status: undefined, code: null };
  }
}
